import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import {
  Button, Card, Input, Spinner,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@agion/ui'
import { Activity, ArrowLeft, Loader2, Search } from 'lucide-react'
import { OperationStatusBadge } from './InfraOperationStatusBadge'
import { OperationLogViewer } from './InfraOperationLogViewer'
import { ProvisionProgress } from './InfraProvisionProgress'
import { OperationOutcome } from './InfraOperationOutcome'
import { TerraformRecoveryPanel } from './TerraformRecoveryPanel'
import { HostUpgradeStagePanel } from './HostUpgradeStagePanel'
import { HostUpgradeOverview } from './HostUpgradeOverview'
import { HostDbLoginsPanel } from './HostDbLoginsPanel'
import { getOperation, listOperations } from './infra_operation.service'
import type { InfraOperation, OperationLogLine, OperationStatus } from '@/core/infra_types'
import { formatDate, formatDuration } from '@/core/infra_utils'

export function OperationsPage() {
  const { id: operationId } = useParams<{ id: string }>()

  if (operationId) {
    return <OperationDetailView operationId={operationId} />
  }

  return <OperationListView />
}

function OperationDetailView({ operationId }: { operationId: string }) {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [operation, setOperation] = useState<InfraOperation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [logs, setLogs] = useState<OperationLogLine[]>([])
  const isProvision = operation?.operationType === 'provision'
  const isHostUpgrade = operation?.operationType === 'host_upgrade'
  const isRunning = operation?.status === 'running' || operation?.status === 'pending'
  const duration = formatDuration(operation?.startedAt, operation?.completedAt)
  // Host_upgrade gets a tabbed view (Overview / Stages / Logs). Other operation
  // types keep the existing single-stream layout. Provision retains its dedicated
  // ProvisionProgress component above the log viewer.
  const [activeTab, setActiveTab] = useState<'overview' | 'stages' | 'logs'>('overview')

  useEffect(() => {
    void (async () => {
      try {
        const op = await getOperation(operationId)
        setOperation(op)
      } catch {
        // Operation not found
      } finally {
        setIsLoading(false)
      }
    })()
  }, [operationId])

  // Re-fetch operation when SSE reports terminal status
  const handleTerminalStatus = useCallback(async (sseStatus: OperationStatus) => {
    try {
      const freshOp = await getOperation(operationId)
      setOperation(freshOp)
    } catch {
      // Fallback: update status from SSE
      setOperation(prev => prev ? { ...prev, status: sseStatus } : prev)
    }
  }, [operationId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <Activity className="text-blue-600" size={28} />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="page-title">
                {operation?.operationType ?? t('pages:operations.defaultType')}
              </h1>
              {operation && <OperationStatusBadge status={operation.status} />}
            </div>
            <p className="page-description text-xs font-mono">{operationId}</p>
          </div>
        </div>
      </div>

      {operation && (
        <Card className="mb-4">
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-slate-500 dark:text-slate-400">
                {t('pages:operations.environmentId', 'Environment')}
              </span>
              <p className="font-mono text-xs mt-1">{operation.environmentId}</p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">
                {t('pages:operations.createdBy', 'Created by')}
              </span>
              <p className="mt-1">{operation.createdBy}</p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">
                {t('pages:operations.createdAt', 'Created')}
              </span>
              <p className="mt-1">{formatDate(operation.createdAt)}</p>
            </div>
            {operation.startedAt && (
              <div>
                <span className="text-slate-500 dark:text-slate-400">
                  {t('pages:operations.startedAt', 'Started')}
                </span>
                <p className="mt-1">{formatDate(operation.startedAt)}</p>
              </div>
            )}
            {operation.completedAt && (
              <div>
                <span className="text-slate-500 dark:text-slate-400">
                  {t('pages:operations.completedAt', 'Completed')}
                </span>
                <p className="mt-1">{formatDate(operation.completedAt)}</p>
              </div>
            )}
            {duration && (
              <div>
                <span className="text-slate-500 dark:text-slate-400">
                  {t('pages:operations.duration', 'Duration')}
                </span>
                <p className="mt-1 font-mono text-xs">{duration}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {operation && <TerraformRecoveryPanel key={operation.id} operation={operation} onRefresh={() => handleTerminalStatus('cancelled')} />}

      {isProvision && (
        <ProvisionProgress
          logs={logs}
          operationStatus={operation?.status ?? 'pending'}
        />
      )}

      {!isProvision && !isHostUpgrade && isRunning && (
        <Card className="mb-4">
          <div className="p-4 flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t(`pages:operations.progress.${operation?.operationType}`, operation?.operationType ?? '')}
            </span>
          </div>
        </Card>
      )}

      {operation && <HostDbLoginsPanel operation={operation} />}

      {isHostUpgrade ? (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList>
            <TabsTrigger value="overview">{t('pages:operations.tabs.overview', 'Overview')}</TabsTrigger>
            <TabsTrigger value="stages">{t('pages:operations.tabs.stages', 'Stages')}</TabsTrigger>
            <TabsTrigger value="logs">{t('pages:operations.tabs.logs', 'Logs')}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-3">
            {operation && <HostUpgradeOverview operation={operation} />}
          </TabsContent>
          <TabsContent value="stages" className="mt-3">
            <HostUpgradeStagePanel
              operationId={operationId}
              onViewLogs={() => setActiveTab('logs')}
            />
          </TabsContent>
          <TabsContent value="logs" className="mt-3">
            <OperationLogViewer
              operationId={operationId}
              enabled={!!operation}
              onTerminalStatus={handleTerminalStatus}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <OperationLogViewer
          operationId={operationId}
          enabled={!!operation}
          onLogsChange={isProvision ? setLogs : undefined}
          onTerminalStatus={handleTerminalStatus}
        />
      )}

      <OperationOutcome
        status={operation?.status ?? null}
        operation={operation}
      />
    </div>
  )
}

const STATUS_OPTIONS: Array<{ value: OperationStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
]

function OperationListView() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [operations, setOperations] = useState<InfraOperation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const loadOperations = useCallback(async () => {
    try {
      setIsLoading(true)
      const params: Record<string, string | number> = { perPage: 50 }
      if (statusFilter && statusFilter !== 'all') params['status'] = statusFilter

      const result = await listOperations(params as Parameters<typeof listOperations>[0])
      setOperations(Array.isArray(result.data) ? result.data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load operations')
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    void loadOperations()
  }, [loadOperations])

  const filtered = operations.filter(op =>
    op.operationType.toLowerCase().includes(searchQuery.toLowerCase()) ||
    op.createdBy.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Activity className="text-blue-600" size={28} />
          <div>
            <h1 className="page-title">{t('pages:operations.title', 'Operations')}</h1>
            <p className="page-description">
              {t('pages:operations.description', 'Track infrastructure provisioning and management operations')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('pages:operations.search', 'Search operations...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t('pages:operations.filters.allStatuses', 'All Statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:operations.filters.allStatuses', 'All Statuses')}</SelectItem>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(`pages:operations.status.${opt.value}`, opt.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {t('pages:operations.empty', 'No operations found.')}
          </div>
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:operations.columns.status', 'Status')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.type', 'Operation')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.environment', 'Environment')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.createdBy', 'Created by')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.created', 'Created')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.error', 'Error')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {filtered.map(op => (
              <TableRow
                key={op.id}
                variant="modern"
                className="cursor-pointer"
                onClick={() => navigate(`/operations/${op.id}`)}
              >
                <TableCell><OperationStatusBadge status={op.status} /></TableCell>
                <TableCell className="font-medium">{op.operationType}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{op.environmentId}</TableCell>
                <TableCell className="text-slate-500">{op.createdBy}</TableCell>
                <TableCell className="text-slate-500 text-xs">{formatDate(op.createdAt)}</TableCell>
                <TableCell className="text-red-500 text-xs max-w-xs truncate">
                  {op.errorMessage ?? ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
